import { useEffect, useState } from "react"

import "./GameRail.css"

/* The left dock, for windows that cannot afford one.

   The dock's contents — the city/move/troop tabs and the minimap — are *reference*: you open the
   city list when you are picking a city, not continuously, and the minimap answers "where am I"
   once. So on a narrow window they stop holding 340px of permanent width and become a 56px rail
   of icons whose panel opens over the map and closes again. At 1024px that is the difference
   between 334px of map and 688px.

   Over, not beside, and that is the whole trick: a panel that pushed the map would resize the
   canvas twice per visit — and the canvas re-centres its buffer on a resize, so every glance at
   the city list would move the map under the player.

   The rail owns which panel is open and the chrome around it; the panels themselves are passed
   in, so the Game view still composes the same components it composes on a desktop and there is
   no second copy of any of them. */

const GameRail = ({ panels, ariaLabel }) => {
  const [openId, setOpenId] = useState(null)
  const open = panels.find((panel) => panel.id === openId) || null

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenId(null)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open])

  // A panel whose owner stopped offering it (a spectator has no tabs) must not stay open with
  // nothing in it.
  useEffect(() => {
    if (openId && !panels.some((panel) => panel.id === openId)) setOpenId(null)
  }, [openId, panels])

  return (
    <>
      <nav className="game-rail" aria-label={ariaLabel}>
        {panels.map((panel) => (
          <button
            key={panel.id}
            type="button"
            className={`rail-button ${panel.id === openId ? "selected" : ""}`}
            aria-expanded={panel.id === openId}
            aria-label={panel.label}
            title={panel.label}
            onClick={() => {
              const next = openId === panel.id ? null : panel.id
              setOpenId(next)
              // Several buttons may open one panel on different tabs — see the Game view's
              // Cities/Moves/Troops, which are three doors into the same one.
              if (next && panel.onOpen) panel.onOpen()
            }}
          >
            {panel.icon}
            <span className="rail-label">{panel.label}</span>
            {panel.badge ? <span className="rail-badge">{panel.badge}</span> : null}
          </button>
        ))}
      </nav>

      {open && (
        <div className="rail-panel" role="dialog" aria-label={open.label}>
          <header className="rail-panel-head">
            <h3>{open.label}</h3>
            <button
              type="button"
              className="rail-panel-close"
              onClick={() => setOpenId(null)}
              aria-label={open.closeLabel}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </header>
          <div className="rail-panel-body">{open.node}</div>
        </div>
      )}
    </>
  )
}

export default GameRail
